import Address from '@/Model/Address';
import { IRADApplication } from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import { ILoader } from '@/Utility/Loader';
import Messenger from '@/Utility/Messenger';
import { IRADEnvironmentProxy } from '@/Communication/RADEnvironmentProxy';
import StorageHelper from '@/Utility/StorageHelper';
import Utilities from '@/Utility/Utilities';
import { IDetailViewInformation, IHistoryState, IHistoryStateStackItem } from '#/CoreFrameInterfaces';
import ViewFactory from '@/Factories/ViewFactory';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import ViewFactoryTuple from '@/Factories/ViewFactoryTuple';
import EventHelper from '@/Utility/EventHelper';
import { inject, singleton } from "tsyringe";
import { type IUserProvider } from "@/Infrastructure/UserProvider";
import { INavigationService } from "@/Management/NavigationService";

/**
 * Analysiert die URL-Anfragen an den Browser und erzeugt die registrierte ViewFactory
 */
@singleton()
export class NavigationServiceImpl implements INavigationService {

    private login: Address;
    private logout: Address;
    private changePassword: Address;
    private twoFA: Address;
    private twoFAInitialConfig: Address;
    private home: Address;
    private start: Address;
    private baseUrl: string;

    // Hilfsvariable, um nach Rückwärtsnavigation eine Aktion ausführen zu können
    private _onNavigateBack;

    // Hilfsvariable, um eine Navigation zu erzwingen, trotz offenem ModalWindow
    private _forceNavigate;

    /**
     * Verwaltet die aktiven ViewFactories
     * Id => Instanz
     */
    private activeFactories: { [id: string]: ViewFactory };

    /**
     * Verwaltet die zuletzt aufgerufenen ViewFactories
     * Name => Instanz
     */
    private lastFactories;

    /**
     * Verwaltet die derzeit angezeigte ViewFactory
     */
    private visibleFactory;

    /**
     * Beinhaltet die weiterleitende Adresse
     */
    private redirectingAddress;

    /**
     * Beinhaltet die initiale Einstiegs-Adresse
     */
    private initialAddress;

    /**
     * Gibt an, ob gerade zurücknavigiert wird; wird ggf. für Stack-Verwaltung benötigt, um Ereignisse deuten zu können
     */
    private _isNavigatingBack;

    /**
     * Wird bei Navigationen entsprechend hoch/runtergezählt
     * Dies ermöglicht die Erkennung von Rückwärts/Vorwärtsnavigationen durch die Browser-Buttons
     */
    private stateCounter;

    historyStateStack: IHistoryStateStackItem[];

    private viewFactories;

    private doLogoutOnReload: boolean;

    /**
     * Enthält Informationen zum aktuellen DetailViewStack
     */
    detailViewStack: KnockoutComputed<IDetailViewInformation[]>;

    /**
     * Wird zum z.B. zum Neuauswerten des DetailViewStacks benutzt
     */
    private statesChanged: KnockoutObservable<boolean>;

    constructor(
        @inject("UserProvider") private userProvider: IUserProvider,
        @inject("RADApplication") private app: IRADApplication,
        @inject("RADEnvironmentProxy") private proxy: IRADEnvironmentProxy,
        @inject("Loader") private loader: ILoader,
    ) {
        this.login = new Address('Login');
        this.logout = new Address('Logout');
        this.changePassword = new Address('ChangePassword');
        this.twoFA = new Address('TwoFactorSelfService');
        this.twoFAInitialConfig = new Address('TwoFactorInitialConfig');
        this.home = null;
        this.start = null;
        this.initialAddress = null;
        this.baseUrl = this.getBaseUrl();
        this._onNavigateBack = null;
        this._forceNavigate = false;
        this.activeFactories = {};
        this.lastFactories = {};
        this.visibleFactory = null;
        this.redirectingAddress = null;
        this._isNavigatingBack = false;
        this.stateCounter = 0;
        this.historyStateStack = [];
        this.viewFactories = [];
        this.doLogoutOnReload = true;
        this.statesChanged = ko.observable(false);
        this.detailViewStack = this.getDetailViewStack();
    }

    /**
     * Intern: Erzeugt das Computed, welches aus dem HistoryStateStack den DetailViewStack baut
     */
    private getDetailViewStack(): KnockoutComputed<IDetailViewInformation[]> {
        return ko.computed(() => {
            // Wird als Dependency hinzugefügt, um das Computed neu zu berechnen, da der HistoryStateStack kein Observable ist
            this.statesChanged();

            let detailViewStack = [];

            for (let x = this.historyStateStack.length - 1; x >= 0; x--) {
                const currentHistoryState = this.historyStateStack[x];
                // Sobald keine DV im State zu finden ist, wird abgebrochen
                if (currentHistoryState.state.name !== 'DetailView')
                    break;
                detailViewStack.splice(0,
                    0,
                    {
                        ergName: currentHistoryState.state?.additionalInformation?.ergName
                            ? currentHistoryState.state.additionalInformation.ergName
                            : '',
                        principalName: currentHistoryState.state?.additionalInformation?.principalName
                            ? currentHistoryState.state.additionalInformation.principalName
                            : '',
                        pdoId: currentHistoryState.state?.additionalInformation?.pdoId
                            ? currentHistoryState.state.additionalInformation.pdoId
                            : ''
                    });
                //Wenn bei der DV ein neuer Stack erzeugt werden soll, wird abgebrochen
                if (currentHistoryState.state?.additionalInformation?.navigationReset === true)
                    break;
            }

            return detailViewStack;
        });
    }

    /**
     * Fügt Informationen zum aktuellen History-State hinzu
     * Diese Funktion ist rein additiv, bestehende Werte nicht überschrieben!
     * @param additionalInformation
     */
    addInformationToCurrentState(additionalInformation: { [key: string]: any }): void {
        const lastState = this.getLastHistoryStateStackItem();
        if (lastState == null)
            return;
        if (!lastState.state.additionalInformation)
            lastState.state.additionalInformation = {};

        const state = $.extend({}, additionalInformation, lastState.state.additionalInformation);
        lastState.state.additionalInformation = state;
        this.statesChanged.notifySubscribers(true);
    }

    /**
     * Gibt zusätzliche Informationen zum aktuellen History-State zurück.
     */
    getInformationFromCurrentState(key: string): any {
        const additionalInformation = this.getLastHistoryStateStackItem()?.state?.additionalInformation;
        if (!additionalInformation)
            return null;
        return additionalInformation[key];
    }

    getLastHistoryStateStackItem(): IHistoryStateStackItem {
        return _.last<IHistoryStateStackItem>(this.historyStateStack);
    }

    /**
     * Versucht eine zuletzt aufgerufene Factory anhand des Namen zu finden,
     */
    findLastFactory(name): ViewFactory {
        let lastFactory = this.lastFactories[name];

        // Falls wir auf eine inzwischen disposed-e Factory stoßen, räumen wir noch mal auf
        if (lastFactory != null && lastFactory.isDisposed) {
            delete this.lastFactories[name];
            lastFactory = null;
        }

        return lastFactory;
    }

    /**
     * Entfernt eine aktive Factory indem diese zunächst disposed wird
     * und anschließend aus dem Objekt entfernt wird
     */
    private removeActiveFactory(name) {
        let activeFactory = null;

        for (var id in this.activeFactories) {
            if (this.activeFactories[id].name === name) {
                activeFactory = this.activeFactories[id];
                break;
            }
        }

        this.removeViewFactory(activeFactory);
    }

    /**
     * Registriert eine neue aktive Factory
     * indem diese ins Objekt eingetragen wird
     */
    private registerActiveFactory(factory: ViewFactory) {
        this.activeFactories[factory.id] = factory;
    }

    /**
     * Registriert eine neue zuletzt aufgerufene Factory
     * indem diese ins Objekt eingetragen wird
     */
    private registerLastFactory(factory, altName) {
        if (factory.name != 'ListView' && factory.name != 'DetailView') {
            this.lastFactories[altName] = factory;
        } else {
            this.lastFactories[factory.name] = factory;
        }
    }

    /**
     * Registriert eine neue zuletzt aufgerufene Factory
     * indem diese ins Objekt eingetragen wird
     */
    private registerVisibleFactory(factory) {
        this.visibleFactory = factory;
    }

    /**
     * Läd eine ViewFactory
     */
    private _loadViewFactory(name, viewFactory: ViewFactory, tryToReuse, disposeOther, address) {
        if (disposeOther !== false && tryToReuse !== true) {
            this.removeActiveFactory(viewFactory.name);
        }

        this.registerActiveFactory(viewFactory);
        this.registerLastFactory(viewFactory, address ? address.toString() : viewFactory.name);
        this.registerVisibleFactory(viewFactory);

        this.renderView(viewFactory);
    }

    private renderView(viewFactory: ViewFactory) {
        const region = viewFactory.getRegion();
        if (!region)
            throw `No region supplied by ViewFactory '${viewFactory.constructor.name}'`;

        if (viewFactory.shouldReplaceRegion())
            this.cleanRegion(region);

        if (AsyncTypeGuards.isAsyncTupleProvider<ViewFactoryTuple>(viewFactory)) {
            viewFactory.getTupleAsync().then(tuple => {
                this.addToRegion(region, tuple.view);
            });
        } else {
            const tuple = viewFactory.getTuple();
            this.addToRegion(region, tuple.view);
        }
    }

    /**
     * Berechnung der Home-Adresse
     */
    private getHomeAddress() {
        return new Address('/Home');
    }

    /**
     * Berechnung der Start-Adresse
     */
    private getStartAddress() {
        if (MODEL.StartViewName) {
            let browserPath = window.location.pathname.toLowerCase();
            let startpage = MODEL.StartViewName.toLowerCase();
            let idx = browserPath.indexOf(startpage, browserPath.length - startpage.length);
            
            const addressToRestore = this.popAddressFromLocalStorage();
            if (addressToRestore != null)
                return addressToRestore;

            if (idx > -1) {
                return new Address(window.location.pathname + window.location.search);
            }
        }

        return new Address('');
    }

    /**
     * Berechnung der BaseURL
     */
    private getBaseUrl() {
        let baseUrl = window.location.pathname;

        if (MODEL.StartViewName) {
            let browserPath = window.location.pathname.toLowerCase();
            let startpage = MODEL.StartViewName.toLowerCase();
            let idx = browserPath.indexOf(startpage, browserPath.length - startpage.length);

            if (idx > -1) {
                baseUrl = window.location.pathname.substr(0, idx);
            }
        }

        if (baseUrl.indexOf('/', baseUrl.length - 1) == -1) {
            baseUrl += '/';
        }

        return baseUrl;
    }

    isNavigatingBack(): boolean {
        return this._isNavigatingBack;
    }

    public setBackNavigation(): void {
        this._isNavigatingBack = true;
    }

    /**
     * Liefert die derzeitig öffentlich sichtbare Adresse
     * (enthält nicht die ggf. versteckten Parameter)
     */
    getAddress() {
        return new Address(window.location.pathname + window.location.search);
    }

    getInitialAddress(): Address {
        return this.initialAddress;
    }

    /**
     * Basis-Implementierung für das Setzen von normalen und Hidden-Parameter
     * @param contextIdentifier
     * @param parameterName
     * @param value
     * @param hidden
     */
    private setAnyParameterToAddress(contextIdentifier: string, parameterName: string, value: any, hidden: boolean) {
        const addr = this.getStatefulAddress();

        // Referenz in LastFactories entfernen
        const addrString = addr.toString();
        const viewFactory = this.findLastFactory(addrString)
        if (viewFactory != null) {
            delete this.lastFactories[addrString];
        }

        // Eigentlichen Parameter setzen
        if (hidden) {
        addr.setHiddenParameter(parameterName, value);
        }
        else {
            addr.setParameter(parameterName, value);
        }

        // Referenz in LastFactories neu setzen
        this.lastFactories[addr.toString()] = viewFactory;

        const state = this.convertAddressToHistoryState(addr);
        const lastState = _.last(this.historyStateStack) as any;

        if (lastState.state?.additionalInformation != null && state.additionalInformation == null) {
            state.additionalInformation = lastState.state.additionalInformation
        }

        if (!hidden) {
            lastState.url = addr.getPublicURL();
        }

        lastState.state = state;

        // Da sich die sichtbare URL ändert, muss hier der State der History geändert werden
        // bei Hidden-Parametern war dies nicht erforderlich
        if (!hidden) {
            history.replaceState(lastState.state.counter, lastState.title, lastState.url);
        }

        Messenger.createMessageAndSend('CurrentAddressChangedMessage', { changedAddress: addr, contextIdentifier: contextIdentifier });
    }

    /**
     * Setzt / Ändert einen Hidden-Parameter der aktuellen Adresse
     */
    setHiddenParameterToAddress(contextIdentifier: string, parameterName: string, value: any): void {
        this.setAnyParameterToAddress(contextIdentifier, parameterName, value, true);
    }

    /**
     * Setzt / Ändert einen Parameter der aktuellen Adresse
     * @param contextIdentifier
     * @param parameterName
     * @param value
     */
    setParameterToAddress(contextIdentifier: string, parameterName: string, value: string): void {
        this.setAnyParameterToAddress(contextIdentifier, parameterName, value, false);
    }

    /**
     * Convenience-Funktion, um eine Menge an Parametern der aktuellen Adresse zu setzen
     * @param contextIdentifier
     * @param parameters
     * @param removeOther
     */
    setParametersToAdress(contextIdentifier: string, parameters: {
        [key: string]: string
    }, removeOther: boolean = false): void {
        let newParameters = {};

        if (removeOther) {
            const addr = this.getStatefulAddress();
            const currentParameters = addr.getParameters();
            _.keys(currentParameters).forEach(key => {
                currentParameters[key] = null;
            });
            $.extend(newParameters, currentParameters, parameters);
        } else {
            newParameters = parameters;
        }

        _.keys(newParameters).forEach((key: string) => {
            this.setParameterToAddress(contextIdentifier, key, parameters[key]);
        });
    }

    /**
     * Liefert die letzte zur Navigation verwendete Addresse
     * (inklusive ggf. versteckter Parameter)
     */
    getStatefulAddress() {
        let state = _.last(this.historyStateStack) as any;
        if (state == null)
            return null;
        return this.convertHistoryStateToAddress(state.state);
    }

    private convertHistoryStateToAddress(state: IHistoryState, isBackwardNavigation?: boolean): Address {
        const address = new Address();
        address.setName(state.name);
        address.setParameters(state.parameters);

        // Wenn auf eine DetailView zurücknavigiert wird, die im CREATE-Modus aufgerufen wurde, muss diese nun im EDIT-Modus aufgerufen werden
        // zusätzlich muss die PDO-ID es zu bearbeitenden Objektes angefügt werden
        if (isBackwardNavigation && state.name === 'DetailView') {
            address.setParameter('Action', 'EDIT');
            address.setParameter('PdoId', state.additionalInformation['pdoId']);
        }

        address.setHiddenNames(state.hiddenNames);
        return address;
    }

    private convertAddressToHistoryState(address: Address): IHistoryState {
        return {
            counter: Date.now(),
            name: address.getName(),
            parameters: address.getParameters(),
            hiddenNames: address.getHiddenNames()
        }
    }

    private go(state, title, url) {
        if (this.isNewState(state, title, url)) {
            this.historyStateStack.push({ state: state, title: title, url: url });
            history.pushState(state.counter, title, url);
        } else {
            // State synchronisieren
            history.replaceState(state.counter, title, url);
        }
    }

    private isNewState(state, title, url) {
        let last = _.last(this.historyStateStack) as any;

        if (last
            && _.isMatch(state.parameters, last.state.parameters)
            && state.name == last.state.name
            && last.title == title)
            return false;

        return true;
    }

    private revertNavigation() {
        var previous = _.last(this.historyStateStack) as any;

        history.pushState(Date.now(), previous.title, previous.url);
    }


    /**
     * Bei einer Rückwärtsnavigation wird immer replace aufgerufen
     */
    private back() {
        this.historyStateStack.pop();
        let previous = _.last(this.historyStateStack) as any;
        if (previous) {
            history.pushState(Date.now(), previous.title, previous.url);
        }
    }

    /**
     * Sucht die zu einem DOM-Element gehörende ViewFactory
     */
    findFactory(element: JQuery): ViewFactory {
        let factoryId = element.closest('[data-factory]').data('factory');

        if (factoryId != null) {
            return this.activeFactories[factoryId];
        }

        return null;
    }

    /**
     * Läd eine ViewFactory anhand ihres Namen
     */
    loadViewFactory(name: string, params?, tryToReuse?: boolean, address?: Address, disposeOther?: boolean): ViewFactory {
        this.loader.show();

        Messenger.createMessageAndSend('ViewFactoryLoadingMessage', {name: name});

        let viewFactory = null;

        if (tryToReuse === true) {
            if (name != 'ListView' && name != 'DetailView') {
                viewFactory = this.findLastFactory(address.toString());
            } else {
                viewFactory = this.findLastFactory(name);
            }
        }

        if (viewFactory != null) {
            // Jetzt Refresh der Factory durchführen; wenn dieser nicht erfolgreich ist, so bauen wir die Factory neu auf
            // Es gibt noch alte ViewFactories, die gar nichts zurückgeben; für diese soll auch das alte Verhalten bestehen bleiben, d.h.
            // wir prüfen nur, ob explizit false zurückgegeben wird.
            if (viewFactory.refresh() === false) {
                // refresh war nicht erfolgreich; baue Factory neu auf
                if (viewFactory != null && !viewFactory.isDisposed) {
                    viewFactory.dispose();
                }
                viewFactory = null;
            }
        }

        if (viewFactory == null) {
            viewFactory = this.loadViewFactoryIsolated(name, params, false);
        }

        if (viewFactory != null) {
            var redirect = viewFactory.getNecessaryRedirect();

            if (redirect) {
                this.removeViewFactory(viewFactory);
                this.redirectingAddress = address;

                this.navigateToAddress(new Address(redirect));
            } else {
                this._loadViewFactory(name, viewFactory, tryToReuse, disposeOther, address);
            }

            this.loader.hide();
            if (this.isResponsiveLayout()) {
                this.showOrHideWireframe();
            }
            return viewFactory;
        } else {
            this.loader.hide();
            if (this.isResponsiveLayout()) {
                this.showOrHideWireframe();
            }
            throw 'ViewFactory "' + name + '" could not be found.';
        }
    }

    /**
     * Berücksichtigt ein vorhandenes WireFrame bei einer Navigation über eine mobile bzw. kleinere Ansicht
     */
    private handleWireFrameOnNavigation(shouldBeExpanded: boolean) {
        if ((StorageHelper.get('WireframeExpanded') != shouldBeExpanded) && this.isResponsiveLayout()) {
            StorageHelper.set('WireframeExpanded', shouldBeExpanded);
            $(window).trigger('resize');
        }
    }

    private isResponsiveLayout() {
        return Utilities.isMobileView();
    }

    private showOrHideWireframe() {
        const shouldBeExpanded = !Utilities.isMobileView();
        if (StorageHelper.get('WireframeExpanded') == shouldBeExpanded)
            return;

        StorageHelper.set('WireframeExpanded', shouldBeExpanded);
        $(window).trigger('resize');
    }

    /**
     * Liefert den Identifier der sichtbaren ViewFactory
     */
    getVisibleViewIdentifier(): string {
        if (this.visibleFactory) {
            return this.visibleFactory.identifier;
        }
        return null;
    }

    /**
     * Entfernt eine ViewFactory.
     */
    removeViewFactory(viewFactory: ViewFactory) {

        if (viewFactory == null) {
            return;
        }

        delete this.activeFactories[viewFactory.id];
        delete this.lastFactories[viewFactory.name];
        if (viewFactory == this.visibleFactory) {
            this.visibleFactory = null;
        }

        this.removeViewFactoryIsolated(viewFactory);
    }

    /**
     * Liefert die Anzahl der aktuell vorhandenen modalen ViewFactories.
     */
    getModalViewFactories(): number {
        return this.viewFactories
            .filter((vf) => {
                if (vf.isDisposed) {
                    return false;
                }

                return vf.tuple && vf.viewModel && vf.viewModel.window &&
                    vf.viewModel.window.isModal !== undefined &&
                    (vf.viewModel.window.isModal === true || vf.viewModel.window.isModal() === true)
            })
            .length;
    }

    /**
     * Lädt eine ViewFactory isoliert, d.h. ohne Abhängigkeiten zu setzen oder Registrierungen durchzuführen.
     */
    loadViewFactoryIsolated<T extends ViewFactory>(name: string, params, renderView: boolean): T {
        var viewFactory: T = null;

        var options = _.extend({name: name}, params);
        var ViewFactory = ComponentManager.getFactory(name) as any;

        if (ViewFactory != null) {
            viewFactory = new ViewFactory(options);

            if (renderView) {
                this.renderView(viewFactory);
            }

            this.viewFactories.push(viewFactory);
        }

        return viewFactory;
    }


    /**
     * Löscht eine ViewFactory ohne ggf. abhängige Registrierungen o.Ä. zu beachten.
     */
    removeViewFactoryIsolated(viewFactory: ViewFactory) {
        if (viewFactory.isDisposed !== false)
            return;

        const view = viewFactory.view?.getTuple()?.view;
        if (view) {
            view.detach();
        }
        viewFactory.dispose();
        const i = _.findIndex(this.viewFactories, (vf: any) => {
            return vf.id == viewFactory.id;
        });
        this.viewFactories.splice(i, 1);
        viewFactory = null;
    }

    /**
     * Entfernt ein Element
     */
    private removeNode(node) {
        var element = $(node);

        element.find('*').each(function () {
            $(this).off();
        });

        kendo.unbind(element);
        kendo.destroy(element);

        ko.removeNode(node);
        element.remove();
    }

    addToRegion(region: string, $element: JQuery) {
        const $region = $('[data-region="' + region + '"]');
        if (!$region.length) {
            console.error(`Couldn't find region '${region}'`);
            return;
        }
        $element.appendTo($region);
    }

    /**
     * Säubert eine Region
     */
    cleanRegion(name: string) {
        Messenger.createMessageAndSend('RegionCleaningMessage', {name: name});
        // TODO: Auf TS umstellen
        let self = this;

        $('[data-region="' + name + '"]').children().each(function () {
            var node = this;
            var view = $(node);
            view.detach();

            var id = view.data('factory');
            var viewFactory = self.activeFactories[id];

            if (viewFactory != null) {
                viewFactory.disposeView();
            }

            if (view.is('[data-view]')) {
                window.setTimeout(function () {
                    self.removeNode(node);
                }, 60);
            } else {
                self.removeNode(node);
            }
        });
    }

    /**
     * Navigiert zur vorherigen Seite zurück
     */
    navigateBack(onNavigated?: () => void) {
        this._onNavigateBack = onNavigated;
        window.history.back();
    }

    forceNavigateBack(onNavigated?: () => void) {
        this._forceNavigate = true;
        this._onNavigateBack = onNavigated;
        window.history.back();
    }

    /**
     * Aktualisiert die aktuelle ViewFactory
     */
    refresh() {
        let address = this.getAddress();
        this.navigateToAddress(address, false, true, true);
    }

    continueNavigation(navigationMessage: {
        address: Address,
        pushState: any,
        tryToReuse: boolean,
        isNavigatingBack: boolean,
        isDisposed: boolean
    }) {
        if (navigationMessage.isDisposed && navigationMessage.address == null) {
            return;
        }
        var address = navigationMessage.address;
        var pushState = navigationMessage.pushState;
        var sendMessage = false; // Die Nachricht wurde ja schon geworfen
        var tryToReuse = navigationMessage.tryToReuse;

        this._isNavigatingBack = navigationMessage.isNavigatingBack;
        this.navigateToAddress(address, pushState, sendMessage, tryToReuse);
        this._isNavigatingBack = false;
    }

    private isAutoLoginActivated(): boolean {
        const value = this.app.getSystemParam('Gui.UseAutoLogin') as string;
        return value && value.toLowerCase() === 'true';
    }

    /**
     * Startet die Navigation zu einer Adresse
     */
    navigateToAddress(address, pushState?, sendMessage?: boolean, tryToReuse?: boolean, openInNewWindow?: boolean): boolean {
        if (openInNewWindow) {

            if (this.app.IsReducedView) {
                //We disable ctrl-click in this mode
                return false;
            }

            if (address.hasHiddenParameter() || this.isAutoLoginActivated()) {
                this.pushAddressToLocalStorage(address);
            }

            window.open(this.baseUrl + address.getPublicURL());

            return true;
        }

        let message = null;

        if (sendMessage == null || sendMessage === true) {
            message = Messenger.createMessageAndSend('NavigationStartedMessage', {
                address: address,
                pushState: pushState,
                tryToReuse: tryToReuse,
                isNavigatingBack: this._isNavigatingBack
            });
        }

        if (message == null || !message.isCanceled()) {
            Messenger.createMessageAndSend('OnBeforeNavigationMessage', {});

            var state = this.convertAddressToHistoryState(address);
            var title = this.app.appName();
            var url = this.baseUrl + address.getPublicURL();


            if (!this._isNavigatingBack) {
                this.go(state, title, url);
            } else {
                this.back();
            }

            address.navigated(true);

            this.loadViewFactory(address.getName(), address.getParameters(), tryToReuse, address);

            return true;
        } else {
            return false;
        }
    }


    /**
     * Benachrichtigt über das Zurücksetzen der Navigation.
     * @param {boolean} keepNavigationHistory - Optionaler Parameter, der angibt, ob die Navigation für den aktuellen Zustand beibehalten werden soll.
     * @return {void}
     */
    notifyNavigationReset(keepNavigationHistory: boolean = false): void {
        Messenger.createMessageAndSend('NavigationResetMessage');
        this.addInformationToCurrentState({"navigationReset": !keepNavigationHistory}); //SAGGRC-1383
    }

    /**
     * Navigiert zu der zuletzt weiterleitenden Adresse
     */
    navigateToRedirectingAddress() {
        if (this.redirectingAddress != null) {
            this.navigateToAddress(this.redirectingAddress, true, true, false);
        }
    }

    /**
     * Startet die Navigation zu einer URL
     */
    navigateToUrl(url: string, onNavigated?: () => void, pushState?, sendMessage?: boolean, tryToReuse?: boolean, openInNewWindow = false) {
        if (url && url[0] === '/') {
            url = url.substring(1);
        }

        if (url.indexOf("coreframe:") == 0) {
            this.handleCoreframeUrl(url);
            return;
        }

        var address = new Address(url);
        if (Utilities.isFunction(onNavigated)) {
            address.onNavigated(onNavigated.bind(this));
        }

        this.navigateToAddress(address, pushState, sendMessage, tryToReuse, openInNewWindow);
    }

    /**
     * Verarbeitet eine CoreFrame-URL und führt die hinterlegte Method aus
     */
    handleCoreframeUrl(url: string) {
        var coreframeUrl = url.replace("coreframe:", "");
        coreframeUrl = coreframeUrl.substr(0, coreframeUrl.indexOf("&"));
        var params = coreframeUrl.split("|");

        // Syntax ist coreframe:<npmPackageName>|(<ExportedClass>.)(<FunctionName>)+(|<Parameters>)
        if (params.length !== 2 && params.length !== 3)
            return;

        const pkg = params[0];
        const callChain = params[1];
        const parameters: any[] = params.length == 3 ? JSON.parse(params[2]) : null;

        // import des packages
        import(pkg).then(imp => {
            const chain = callChain.split('.')

            let obj = imp[chain[0]];

            // wenn kein . vorkommt wird dierekt eine Funktion exportiert
            if (chain.length == 1) {
                obj.apply(obj, parameters);
            }

            // Kette ablaufen mit leeren Parametern
            for (let i = 1; i < chain.length - 1; i++) {
                obj = obj[chain[i]]()
            }

            // Funktion aufrufen
            obj[chain[chain.length-1]].apply(obj, parameters);
        });
    }

    /**
     * Startet die Navigation zum Login
     */
    navigateToLogin() {
        this.navigateToAddress(this.login);
    }

    handleUserInputAfterLogin(changePassword: boolean, needsTwoFaRegistration: boolean): void {
        if (changePassword) {
            const callback = needsTwoFaRegistration
                ? this.navigateToTwoFAInitialConfig
                : () => {
                };

            this.navigateToChangePassword(false, callback);
        }
    }

    navigateToChangePassword(allowCloseWindow: boolean = true, callback: () => void = null) {
        let viewFactory = this.loadViewFactoryIsolated(this.changePassword.getName(), {
            onClose: () => {
                this.removeViewFactoryIsolated(viewFactory);
                viewFactory = null;

                if (callback)
                    callback();
            },
            allowClose: allowCloseWindow
        }, true);
    }

    navigateToTwoFAInitialConfig() {
        let viewFactory = this.loadViewFactoryIsolated(this.twoFAInitialConfig.getName(), {
            onClose: () => {
                this.removeViewFactoryIsolated(viewFactory);
                viewFactory = null;
            }
        }, true);
    }

    navigateToTwoFA() {
        let viewFactory = this.loadViewFactoryIsolated(this.twoFA.getName(), {
            onClose: () => {
                this.removeViewFactoryIsolated(viewFactory);
                viewFactory = null;
            }
        }, true);
    }

    /**
     * Startet die Navigation zum Logout
     */
    navigateToLogout() {
        this.navigateToAddress(this.logout);
    }

    /**
     * Startet die Navigation zur initial aufgerufenen Adresse
     */
    navigateToStart() {
        this.navigateToAddress(this.start);
    }

    /**
     * Startet die Navigation zur eingerichteten Startseite
     */
    navigateToHome(openInNewWindow: boolean = false) {
        if (this.app.IsReducedView) {
            this.navigateToLogout();
        } else {
            this.navigateToAddress(this.home, null, null, null, openInNewWindow);
        }
    }

    /**
     * Läd die Basis-Seite der SPA neu
     * @param doLogout Gibt an, ob ein Logout stattfinden soll
     */
    reload(doLogout: boolean = true, goToStartpage: boolean = true) {
        this.doLogoutOnReload = doLogout;
        if (goToStartpage) {
            window.location.href = this.baseUrl;
        } else {
            if (this.isAutoLoginActivated()) {
                // Speichert die Adresse unter einer ID im Storage und gibt diese ID in die URL aus.
                // Dadurch erkennt der NavigationService, das dies eine Navigation aus dem Client heraus ist.
                const address = this.getAddress();
                this.pushAddressToLocalStorage(address);
                window.location.href = address.toString();
            } else {
                window.location.reload();
            }
        }
    }

    /*
     * Lädt die Basis-Seite auf Basis eines Redirects neu.
     * Hierbei wird serverseitig ein Logout durchgeführt. Dies ist insbesondere in SSO-Szenarien erforderlich
     */
    reloadAndLogout() {
        this.doLogoutOnReload = false;
        let clientID = -1;

        const user = this.userProvider.getUser();
        if (user)
            clientID = user.clientInfoID();

        window.location.href = this.app.serverUrl + 'endsession?clientid=' + clientID;
    }

    /**
     * Initialisiert den NavigationService und leitet initial an das Login-Modul weiter
     * Registriert eine Funktion an den Browserevents "Vor" und "Zurück" um vorherigen Zustand wiederherstellen zu können
     */
    init(): void {
        this.home = this.getHomeAddress();
        this.start = this.getStartAddress();
        this.initialAddress = this.getStartAddress();

        if (this.start.isEqualTo([this.login])) {
            this.start = this.home;
        }

        if (!MODEL.IsLinkAllowed) {
            // Wenn dies ein AutoLogin ist und die Adresse im Storage gefunden (restored) wurde,
            // dann erlauben wir die Navigation, da es sich nicht um einen DeepLink handelt.
            if (MODEL.TryAutoLogin && this.start.getIsRestored()) {
                MODEL.IsLinkAllowed = true;
            } else {
                this.start = this.home;
            }
        }

        this.start.onNavigated(this.notifyNavigationReset.bind(this));
        if (this.home != this.start) {
            this.home.onNavigated(this.notifyNavigationReset.bind(this));
        }

        $(window).on('popstate', this.onPopState.bind(this));
        $(window).on('beforeunload', this.onUnload.bind(this));

        // TODO: Auf TS umstellen
        let self = this;
        $('body').on('click', 'a', function (event) {
            var $this = $(this);

            var href = $this.attr('href');
            if (!EventHelper.isIgnoredByRADEventHandlers(this) && !((this as HTMLAnchorElement).target == '_blank') && (href && href.indexOf('javascript:') != 0)) {

                event.preventDefault();

                if (href && href !== '#') {
                    var address = new Address(href);
                    self.navigateToAddress(address);
                }
            }
        });

        this.navigateToAddress(this.start);
        //Nach dem der HTML-Client gestartet wurde, werden alle historyState geleert, sodass BACK nur noch zu Home führen kann
        this.historyStateStack = [];
    }

    /**
     * Bei jeder durch Browser-Buttons verursachten Änderung des Browser-History-Stacks wird diese Methode aufgerufen
     * Ein manuelles Aufrufen von pushState (wie in self.setState) löst dies nicht aus.
     * @param event
     */
    private onPopState(event: JQuery.TriggeredEvent) {
        const now = Date.now();
        const originalEvent = event.originalEvent as PopStateEvent;
        let eventState = originalEvent.state;

        const isBackwardNavigation = eventState < now;
        const historyLength = this.historyStateStack.length;

        var fallbackToHome = eventState == null || (isBackwardNavigation && historyLength < 2);
        if (fallbackToHome) {
            this.historyStateStack = [];
            this.navigateToHome();
            return;
        }

        if (isBackwardNavigation && historyLength > 0) {
            let headPoint = this.historyStateStack[historyLength - 1];
            //Wenn von einem Workflow mit navigationReset zurücknavigiert landet man auf der Start-Seite CF-92652
            if (headPoint.state?.additionalInformation?.navigationReset === true) {
                this.historyStateStack = [];
                this.navigateToHome();
                return;
            }
        }

        //stateCounter = eventState.counter;
        // hier muss die adresse von dem eigenen stack kommen!
        if (isBackwardNavigation && historyLength > 1) {
            const navPoint = this.historyStateStack.slice(-2, -1);
            eventState = navPoint[0].state;
        }

        let address = this.convertHistoryStateToAddress(eventState, isBackwardNavigation);

        if (isBackwardNavigation) {
            const _userOnNavigateBack = this._onNavigateBack;
            this._onNavigateBack = null;

            // Wenn wir zurücknavigieren, dann soll die jetzt sichtbare ViewFactory komplett zerstört werden;
            const onNavigated = () => {
                if (this.visibleFactory != null) {
                    this.removeViewFactory(this.visibleFactory);
                }

                if (Utilities.isFunction(_userOnNavigateBack)) {
                    _userOnNavigateBack();
                }
            };

            address.onNavigated(onNavigated);
        }
        else {
            // Vorwärts navigation
            const lastState = _.last(this.historyStateStack) as any;
            history.replaceState(lastState.state, lastState.title, lastState.url);
            return;
        }

        if (address.isEqualTo([this.login, this.logout] as any)) {
            address = this.home;
        }


        this._isNavigatingBack = isBackwardNavigation;
        // Bei einer Navigation über die Pfeiltasten(Browser) ist tryToReuse niemals auf True.
        // Weil die Identifizierung der Wiederherstellbarkeit allein über den Typ der Ansicht erfolgt.
        // Wird also von einer LV zu LV navigiert, wird die falsche LV zur Wiederherstellung verwendet.
        if (this._forceNavigate || !this.app.isModalWindowOpen()) {
            this._forceNavigate = false;
            const hasNavigated = this.navigateToAddress(address, false, true, true);
            if (!hasNavigated)
                this.revertNavigation();
        }
        else {
            this.revertNavigation();
        }

        this._isNavigatingBack = false;
    }

    /**
     * Diese Methode wird vorm Verlassen / Neuladen der Seite aufgerufen
     * und löst bswp. einen AutoLogout aus
     */
    private onUnload(event: JQuery.TriggeredEvent): void {
        if (!this.doLogoutOnReload)
            return;
        const currentPage = this.getAddress();
        //Auf der Loginseite muss kein Logout gefeuert werden.
        if (currentPage === null || !currentPage.isEqualTo([this.login] as any)) {
            const user = this.userProvider.getUser();
            (this.proxy.autoLogout as any)({
                clientInfoID: user.clientInfoID(),
                _async: this.shouldCallAjaxCallsAsyncOnUnload()
            });
        }
    }

    private shouldCallAjaxCallsAsyncOnUnload(): boolean {
        if (Utilities.isIE() || Utilities.isFirefox())
            return false;

        return true;
    }

    private pushAddressToLocalStorage(address: Address): void {
        const uniqueAddressId = Utilities.getUuid();
        let addressesToVisit = StorageHelper.getGlobal('addressesToVisit') || {};
        addressesToVisit = $.extend(addressesToVisit, {
            [uniqueAddressId]: address
        });
        StorageHelper.setGlobal('addressesToVisit', addressesToVisit);
        address.setParameter('address', uniqueAddressId);
    }

    private popAddressFromLocalStorage(): Address {
        const params = window.location.search.slice(1).split('&').map(kv => kv.split('='));
        const matched = params.filter((kv) => kv[0] === 'address');

        if (matched.length === 1) {
            const addressesToVisit = StorageHelper.getGlobal('addressesToVisit') || {};
            if (addressesToVisit.hasOwnProperty(matched[0][1])) {
                const addr = addressesToVisit[matched[0][1]];
                delete addressesToVisit[matched[0][1]];

                StorageHelper.setGlobal('addressesToVisit', addressesToVisit);
                const address = Address.restoreFromStorageAddress(addr);
                address.removeParameter('address');

                return address;
            }
        }

        return null;
    }
}